import { useEffect, useState } from "react";
import { Loader2, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * Stop: halt the agent's running turn now. The API only records the ask; the
 * worker watching the run kills it within a second or two, so the control says
 * "Stopping…" until the agent is no longer working.
 *
 * A hook as well as a button because the chat puts Stop in two places — a small
 * round button beside Send, and "Stop and clear the queue" in the header's ⋯
 * menu — and both must share one "already pressed" state.
 */
export function useStopAgent({
  agentId,
  agentName,
  working,
  stopping: stopRequested = false,
  onStopped,
}: {
  agentId: string;
  agentName: string;
  working: boolean;
  /** The server already has a Stop for this run (pressed elsewhere, or before a reload). */
  stopping?: boolean;
  onStopped?: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  useEffect(() => {
    if (!working) setPressed(false);
  }, [working]);

  const stop = async (clearQueue: boolean) => {
    setPressed(true);
    try {
      const { stoppedTurnId, cleared } = await api.stopAgentTurn(agentId, clearQueue);
      if (!stoppedTurnId) setPressed(false);
      toast.success(
        stoppedTurnId
          ? `Stopping ${agentName}${cleared ? ` and cleared ${cleared} queued` : ""}`
          : `${agentName} had already finished${cleared ? `; cleared ${cleared} queued` : ""}`,
      );
      onStopped?.();
    } catch (cause) {
      setPressed(false);
      toast.error(cause instanceof ApiError ? cause.message : `Could not stop ${agentName}`);
    }
  };

  return { stopping: working && (pressed || stopRequested), stop };
}

type StopControl = ReturnType<typeof useStopAgent>;

/**
 * The small round ■ that sits next to Send while the agent is working. One tap
 * stops the run; with turns queued behind it the tap opens a two-line choice,
 * because whether those should go too is the question.
 */
export function StopRoundButton({
  control,
  agentName,
  queued = 0,
  className,
}: {
  control: StopControl;
  agentName: string;
  queued?: number;
  className?: string;
}) {
  const { stopping, stop } = control;
  const face = (
    <Button
      variant="destructive"
      size="icon-sm"
      className={cn("rounded-full", className)}
      aria-label={stopping ? `Stopping ${agentName}` : `Stop ${agentName}`}
      disabled={stopping}
      onClick={queued === 0 ? () => void stop(false) : undefined}
    >
      {stopping ? <Loader2 className="animate-spin" /> : <Square className="fill-current" />}
    </Button>
  );
  if (stopping) return <Hint label="Stopping…">{face}</Hint>;
  if (queued === 0) return <Hint label={`Stop ${agentName} now. What it already said stays in the chat.`}>{face}</Hint>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{face}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top">
        <DropdownMenuItem onSelect={() => void stop(false)}>Stop this run ({queued} queued carry on)</DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={() => void stop(true)}>
          Stop and clear the queue
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Stop as a self-contained control, for places that are not the agent's chat —
 * the "X is working on this card" line in the task sheet. Icon only by default.
 */
export function StopAgentButton({
  agentId,
  agentName,
  working,
  queued = 0,
  stopping,
  onStopped,
}: {
  agentId: string;
  agentName: string;
  /** The agent has a running turn; the button is only shown while it does. */
  working: boolean;
  /** Turns waiting behind the run. */
  queued?: number;
  stopping?: boolean;
  onStopped?: () => void;
}) {
  const control = useStopAgent({ agentId, agentName, working, stopping, onStopped });
  // Stopping an agent is admin work; the server refuses it for anyone else.
  const canStop = useCan("admin");
  if (!working || !canStop) return null;
  return <StopRoundButton control={control} agentName={agentName} queued={queued} className="size-6 [&_svg]:size-3" />;
}
